// ─────────────────────────────────────────────────────────────
// ZIFEK CRÉDIT — types & utilitaires partagés
// Carnet de crédit numérique pour les commerces de proximité.
// Chaque opération est scopée par `merchant_id` (isolation multi-tenant).
// ─────────────────────────────────────────────────────────────

export type CreditTxType =
  | 'credit'
  | 'payment'
  | 'deposit'
  | 'adjustment'
  | 'transfer_out'
  | 'transfer_in';
export type CreditChannel = 'inapp' | 'sms' | 'whatsapp' | 'email';

export interface CreditCustomer {
  id: number;
  merchant_id: number;
  full_name: string;
  phone: string | null;
  credit_limit: number | null;
  usual_payment_date: string | null;
  notes: string | null;
  linked_user_id: number | null;
  username: string | null;
  temp_password: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface CreditTransaction {
  id: number;
  merchant_id: number;
  customer_id: number;
  type: CreditTxType;
  amount: number;
  description: string | null;
  payment_method: string | null;
  created_by: number | null;
  reverses_transaction_id: number | null;
  /** Photo de preuve associée (produits pris à crédit, reçu…). */
  proof_url?: string | null;
  created_at: string;
}

export interface CreditReminderPrefs {
  new_credit: boolean;
  payment_confirmation: boolean;
  balance: boolean;
  due_date: boolean;
  monthly_statement: boolean;
  channel: CreditChannel;
}

export interface CreditSettings {
  id?: number;
  merchant_id: number;
  currency: string;
  reminders: CreditReminderPrefs | null;
  payment_due_day: number | null;
}

export interface CreditReminder {
  id: number;
  merchant_id: number;
  customer_id: number | null;
  type: string;
  channel: string;
  scheduled_for: string | null;
  status: string;
  payload: Record<string, unknown> | null;
  sent_at: string | null;
  created_at: string;
}

export const DEFAULT_REMINDER_PREFS: CreditReminderPrefs = {
  new_credit: true,
  payment_confirmation: true,
  balance: false,
  due_date: true,
  monthly_statement: true,
  channel: 'inapp',
};

export const CREDIT_CURRENCIES = ['MAD', 'EUR', 'USD', 'XOF', 'XAF', 'DZD', 'TND', 'GBP'];

export const TX_TYPE_META: Record<CreditTxType, { label: string; icon: string; className: string }> = {
  credit: { label: 'Crédit', icon: 'ri-arrow-down-line', className: 'bg-primary-100 text-primary-700' },
  payment: { label: 'Paiement', icon: 'ri-arrow-up-line', className: 'bg-accent-100 text-accent-700' },
  deposit: { label: 'Dépôt de fonds', icon: 'ri-wallet-3-line', className: 'bg-accent-100 text-accent-700' },
  adjustment: { label: 'Ajustement', icon: 'ri-equalizer-line', className: 'bg-secondary-100 text-secondary-700' },
  transfer_out: { label: 'Solde magasin envoyé', icon: 'ri-send-plane-line', className: 'bg-secondary-100 text-secondary-700' },
  transfer_in: { label: 'Solde magasin reçu', icon: 'ri-inbox-archive-line', className: 'bg-accent-100 text-accent-700' },
};

export const PAYMENT_METHODS = ['Espèces', 'Carte', 'Virement', 'Chèque', 'Mobile', 'Autre'];

/** Montant signé d'une transaction : + dette, - paiement/dépôt, ajustement signé. */
export function txSignedAmount(tx: Pick<CreditTransaction, 'type' | 'amount'>): number {
  const amount = Number(tx.amount) || 0;
  if (tx.type === 'payment' || tx.type === 'deposit' || tx.type === 'transfer_in') return -amount;
  if (tx.type === 'adjustment') return amount; // peut être négatif pour corriger
  return amount; // credit / transfer_out augmentent la dette
}

/**
 * Solde « magasin » disponible pour un client chez un commerçant.
 * Un solde de dette négatif signifie que le client a des fonds en dépôt,
 * utilisables uniquement chez ce commerçant.
 */
export function storeBalanceOf(transactions: Pick<CreditTransaction, 'type' | 'amount'>[]): number {
  const net = computeBalance(transactions);
  return Math.max(0, -net);
}

/** Solde courant = somme des montants signés. */
export function computeBalance(transactions: Pick<CreditTransaction, 'type' | 'amount'>[]): number {
  return transactions.reduce((sum, tx) => sum + txSignedAmount(tx), 0);
}

/** Regroupe les transactions par customer_id. */
export function groupByCustomer(
  transactions: CreditTransaction[]
): Record<number, CreditTransaction[]> {
  const map: Record<number, CreditTransaction[]> = {};
  transactions.forEach((tx) => {
    if (!map[tx.customer_id]) map[tx.customer_id] = [];
    map[tx.customer_id].push(tx);
  });
  return map;
}

export function formatMoney(amount: number, currency = 'MAD'): string {
  const value = Number.isFinite(amount) ? amount : 0;
  const formatted = value.toLocaleString('fr-FR', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${formatted} ${currency}`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Prochaine échéance estimée à partir de la date habituelle de paiement. */
export function nextDueDate(usualPaymentDate: string | null): string | null {
  if (!usualPaymentDate) return null;
  const day = parseInt(String(usualPaymentDate).replace(/\D/g, ''), 10);
  if (Number.isNaN(day) || day < 1 || day > 31) return null;
  const now = new Date();
  let due = new Date(now.getFullYear(), now.getMonth(), day);
  if (due.getTime() < now.setHours(0, 0, 0, 0)) {
    due = new Date(now.getFullYear(), now.getMonth() + 1, day);
  }
  return due.toISOString();
}

export interface StatementInput {
  merchantName: string;
  customerName: string;
  currency: string;
  balance: number;
  creditLimit: number | null;
  transactions: CreditTransaction[];
}

/** Génère un relevé de compte texte (partageable, imprimable). */
export function buildStatement(input: StatementInput): string {
  const lines: string[] = [];
  lines.push(`RELEVÉ DE COMPTE — ${input.merchantName}`);
  lines.push(`Client : ${input.customerName}`);
  lines.push(`Date : ${new Date().toLocaleDateString('fr-FR')}`);
  lines.push('------------------------------------------');
  lines.push(`Solde actuel : ${formatMoney(input.balance, input.currency)}`);
  if (input.creditLimit != null) {
    lines.push(`Plafond : ${formatMoney(input.creditLimit, input.currency)}`);
    lines.push(`Disponible : ${formatMoney(Math.max(0, input.creditLimit - input.balance), input.currency)}`);
  }
  lines.push('------------------------------------------');
  input.transactions
    .slice()
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .forEach((tx) => {
      const signed = txSignedAmount(tx);
      const sign = signed < 0 ? '-' : '+';
      lines.push(
        `${formatDate(tx.created_at)} | ${TX_TYPE_META[tx.type].label} | ${sign}${formatMoney(Math.abs(Number(tx.amount)), input.currency)}${tx.description ? ` | ${tx.description}` : ''}`
      );
    });
  lines.push('------------------------------------------');
  lines.push('Merci de votre confiance.');
  return lines.join('\n');
}

/** Données encodées dans le QR du client (permettent au marchand d'ouvrir le profil). */
export function buildCustomerQrData(customerId: number): string {
  return `zifekcredit://customer/${customerId}`;
}

/** Données encodées dans le QR d'invitation du marchand. */
export function buildMerchantInviteQrData(origin: string, token: string): string {
  return `${origin}/credit/join?token=${token}`;
}

/** Extrait un id client depuis un contenu de QR scanné. */
export function parseCustomerQrData(data: string): number | null {
  const match = data.match(/customer\/(\d+)/);
  if (!match) return null;
  const id = parseInt(match[1], 10);
  return Number.isNaN(id) ? null : id;
}

/** Génère un jeton d'invitation aléatoire. */
export function generateToken(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const USERNAME_SAFE = 'abcdefghijklmnopqrstuvwxyz0123456789';

function randomChars(length: number, alphabet: string): string {
  const bytes = new Uint8Array(length);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes)
    .map((b) => alphabet[b % alphabet.length])
    .join('');
}

/** Slugifie un nom pour construire un identifiant lisible. */
function slugifyName(name: string): string {
  return (name || 'client')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 12) || 'client';
}

/**
 * Génère un identifiant (nom d'utilisateur) unique et lisible pour un client.
 * Ex : « Mohammed Alami » → « mohammed.4f9c ».
 */
export function generateUsername(fullName: string): string {
  const base = slugifyName(fullName);
  const suffix = randomChars(4, USERNAME_SAFE);
  return `${base}.${suffix}`;
}

/**
 * Génère un mot de passe par défaut partageable.
 * Alphabet sans caractères ambigus (o/0, l/1/I) pour une saisie facile.
 */
export function generatePassword(length = 8): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  return randomChars(length, alphabet);
}

export interface CreditCredentials {
  username: string;
  password: string;
}

/** Lot d'import de dettes (reprise d'un cahier papier, par photo). */
export interface CreditImportBatch {
  id: number;
  merchant_id: number;
  source: string;
  photo_count: number;
  item_count: number;
  total_amount: number;
  items: { name: string; amount: number; description?: string }[];
  created_customer_ids: number[];
  created_transaction_ids: number[];
  status: string;
  created_at: string;
  reverted_at: string | null;
}